import {
    Component, ChangeDetectionStrategy, inject, signal, computed,
    input, output, ElementRef, viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';

import { LogService } from '../../../../core/services/log.service';
import { TwoFactorAuthService } from '../../../../core/services/two-factor-auth.service';

import { PrimeTemplate } from '@openng/optimus-ui/api';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Button } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';
import { Message } from '@openng/optimus-ui/message';
import { Stepper, StepperModule } from '@openng/optimus-ui/stepper';
import { Tooltip } from '@openng/optimus-ui/tooltip';

type SetupStep = 'loading' | 'scan' | 'verify' | 'success' | 'error';

@Component({
    selector: 'authenticator-setup-modal',
    templateUrl: './authenticator-setup-modal.component.html',
    styleUrls: ['./authenticator-setup-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, Dialog, Button, InputText, ProgressSpinner, Message, StepperModule, TranslatePipe, PrimeTemplate, Tooltip]
})
export class AuthenticatorSetupModalComponent {
    private readonly log = inject(LogService);
    private readonly twoFactorService = inject(TwoFactorAuthService);
    private readonly sanitizer = inject(DomSanitizer);

    codeInput = viewChild<ElementRef>('codeInput');

    // Inputs
    visible = input<boolean>(false);
    token = input.required<string>();

    // Outputs
    visibleChange = output<boolean>();
    registered = output<void>();

    // State
    currentStep = signal<SetupStep>('loading');
    isLoading = signal(false);
    errorMessage = signal<string | null>(null);
    qrCodeImage = signal<string | null>(null);
    secretKey = signal<string | null>(null);
    verificationCode = signal('');
    showSecret = signal(false);
    activeStepIndex = signal(0);

    // Code is always 6 digits
    readonly CODE_LENGTH = 6;

    // Computed
    canVerify = computed(() => {
        return this.verificationCode().length === this.CODE_LENGTH && !this.isLoading();
    });

    isCodeValid = computed(() => {
        const code = this.verificationCode();
        return code.length === 0 || /^\d{6}$/.test(code);
    });

    safeQrCodeUrl = computed((): SafeUrl | null => {
        const image = this.qrCodeImage();
        if (!image) return null;
        if (image.startsWith('data:')) {
            // Safe: data URI is a static base64-encoded image from the TOTP QR code library.
            // bypassSecurityTrustUrl is required because Angular sanitizes data: URIs by default.
            // No user-controlled input reaches this URI — it originates from the server's
            // TwoFactorAuthService.getAuthenticatorQRCode() response.
            return this.sanitizer.bypassSecurityTrustUrl(image);
        }
        // Safe: data URI is a static base64-encoded PNG from the TOTP QR code library.
        // bypassSecurityTrustUrl is required because Angular sanitizes data: URIs by default.
        // No user-controlled input reaches this URI — it originates from the server's
        // TwoFactorAuthService.getAuthenticatorQRCode() response.
        return this.sanitizer.bypassSecurityTrustUrl(`data:image/png;base64,${image}`);
    });

    formattedSecret = computed(() => {
        const secret = this.secretKey();
        if (!secret) return '';
        // Format in groups of 4 for readability
        return secret.match(/.{1,4}/g)?.join(' ') || secret;
    });

    onShow(): void {
        this.currentStep.set('loading');
        this.qrCodeImage.set(null);
        this.secretKey.set(null);
        this.verificationCode.set('');
        this.errorMessage.set(null);
        this.showSecret.set(false);
        this.activeStepIndex.set(0);

        this.loadQRCode();
    }

    private loadQRCode(): void {
        const tokenValue = this.token();
        if (!tokenValue) {
            this.currentStep.set('error');
            this.errorMessage.set('Registration token is missing. Please try again.');
            return;
        }

        this.isLoading.set(true);

        this.twoFactorService.getAuthenticatorQRCode(tokenValue).subscribe({
            next: (response) => {
                this.isLoading.set(false);
                if (response.success && response.image) {
                    this.qrCodeImage.set(response.image);
                    this.secretKey.set(response.secret || null);
                    this.currentStep.set('scan');
                } else {
                    this.currentStep.set('error');
                    this.errorMessage.set(response.message || 'Failed to generate QR code.');
                }
            },
            error: (err) => {
                this.log.error('Error getting authenticator QR code', { error: err instanceof Error ? err.message : String(err) });
                this.isLoading.set(false);
                this.currentStep.set('error');
                this.errorMessage.set('Failed to connect to server. Please try again.');
            }
        });
    }

    proceedToVerify(): void {
        this.currentStep.set('verify');
        this.activeStepIndex.set(1);

        setTimeout(() => {
            this.codeInput()?.nativeElement?.focus();
        }, 100);
    }

    onCodeChange(value: string): void {
        // Only allow digits
        const digitsOnly = value.replace(/\D/g, '').slice(0, this.CODE_LENGTH);
        this.verificationCode.set(digitsOnly);
        this.errorMessage.set(null);

        // Auto-submit when 6 digits are entered
        if (digitsOnly.length === this.CODE_LENGTH) {
            setTimeout(() => this.verifyCode(), 100);
        }
    }

    verifyCode(): void {
        if (!this.canVerify()) return;

        const code = this.verificationCode();
        const tokenValue = this.token();

        this.isLoading.set(true);
        this.errorMessage.set(null);

        this.twoFactorService.verifyAuthenticator(code, tokenValue).subscribe({
            next: (success) => {
                this.isLoading.set(false);
                if (success) {
                    this.currentStep.set('success');
                    this.activeStepIndex.set(2);
                    this.log.info('Authenticator registered successfully');
                } else {
                    this.errorMessage.set('Invalid code. Please try again.');
                    this.verificationCode.set('');
                    setTimeout(() => {
                        this.codeInput()?.nativeElement?.focus();
                    }, 100);
                }
            },
            error: (err) => {
                this.log.error('Error verifying authenticator code', { error: err instanceof Error ? err.message : String(err) });
                this.isLoading.set(false);
                this.errorMessage.set('Failed to verify code. Please try again.');
                this.verificationCode.set('');
            }
        });
    }

    toggleSecretVisibility(): void {
        this.showSecret.update(v => !v);
    }

    copySecret(): void {
        const secret = this.secretKey();
        if (secret) {
            navigator.clipboard.writeText(secret).then(() => {
                this.log.info('Secret copied to clipboard');
            }).catch(err => {
                this.log.error('Failed to copy secret', { error: err instanceof Error ? err.message : String(err) });
            });
        }
    }

    complete(): void {
        this.registered.emit();
        this.close();
    }

    retry(): void {
        this.currentStep.set('loading');
        this.errorMessage.set(null);
        this.activeStepIndex.set(0);
        this.loadQRCode();
    }

    goBackToScan(): void {
        this.currentStep.set('scan');
        this.activeStepIndex.set(0);
        this.verificationCode.set('');
        this.errorMessage.set(null);
    }

    close(): void {
        this.visibleChange.emit(false);
    }
}
